import React, { useState, useEffect, useRef } from 'react';
import { useAccount, useWatchContractEvent, useReadContract, useBalance } from 'wagmi';
import {
    Activity, Power, Terminal, Target,
    ChevronDown, Wallet, Cpu, Lock, History
} from 'lucide-react';
import { DUCK_SIGNALS_ABI, DUCK_SIGNALS_ADDRESS } from '../../config/duckSignalsAbi';
import { NADFUN_CONTRACTS, ERC20_ABI } from '../../config/abi';
import { useSwap } from '../../hooks/useSwap';
import { useRecentSignals } from '../../hooks/useDuckSignals';
import { POPULAR_MONAD_TOKENS, TokenConfig } from '../../lib/WebAgentEngine';
import { useTokenPrice } from '../../hooks/useTokenPrice';

// ═══════════════════════════════════════════════════════════════════════════════
// THEME & STYLES (Matched to LiveAnalysis)
// ═══════════════════════════════════════════════════════════════════════════════

const THEME = {
    purple: '#836EF9',
    purpleLight: '#A08BFF',
    yellow: '#FFD700',
    orange: '#FF8C00',
    green: '#22c55e',
    red: '#ef4444',
    card: 'linear-gradient(145deg, rgba(131, 110, 249, 0.15) 0%, rgba(94, 23, 235, 0.1) 100%)',
    border: 'rgba(255, 215, 0, 0.2)',
    glow: '0 0 40px rgba(255, 215, 0, 0.15)',
};

const styles = {
    card: {
        background: THEME.card,
        border: `1px solid ${THEME.border}`,
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: THEME.glow,
        marginBottom: '1.5rem',
    } as React.CSSProperties,
    header: {
        padding: '1.25rem 1.5rem',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'rgba(0,0,0,0.2)',
    } as React.CSSProperties,
    body: {
        padding: '1.5rem',
    } as React.CSSProperties,
};

// ═══════════════════════════════════════════════════════════════════════════════
// SUBCOMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const TerminalLog: React.FC<{ logs: string[] }> = ({ logs }) => {
    const endRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [logs]);

    return (
        <div className="font-mono text-xs h-64 overflow-y-auto p-4 rounded-xl bg-black/50 border border-white/5 custom-scrollbar">
            {logs.length === 0 && (
                <div className="text-gray-500 italic text-center mt-24">
                    System initialized. Waiting for signals...
                </div>
            )}
            {logs.map((log, i) => (
                <div key={i} className="mb-1.5 breaking-words leading-relaxed" dangerouslySetInnerHTML={{ __html: log }} />
            ))}
            <div ref={endRef} />
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const AutoTrader: React.FC = () => {
    const { isConnected, address } = useAccount();
    const { executeBuy, executeSell, approveToken } = useSwap();

    // State
    const [isArmed, setIsArmed] = useState(false);
    const [tradeAmount, setTradeAmount] = useState('1'); // Amount in MON (buy) or Token (sell)
    const [logs, setLogs] = useState<string[]>([]);
    const [confidenceThreshold, setConfidenceThreshold] = useState(75);
    const [targetToken, setTargetToken] = useState<TokenConfig>(POPULAR_MONAD_TOKENS[0]);
    const [isTokenDropdownOpen, setIsTokenDropdownOpen] = useState(false);
    const [tradeHistory, setTradeHistory] = useState<{ type: string, amount: string, price: string, time: number }[]>([]);

    // Data Hooks
    const priceData = useTokenPrice(targetToken);
    const { signals } = useRecentSignals(5);

    // Wallet Balance for selected token
    const { data: tokenBalance } = useBalance({
        address: address,
        token: targetToken.address as `0x${string}`,
    });

    // Allowance Check
    const { data: allowance } = useReadContract({
        address: targetToken.address as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [address || '0x0', NADFUN_CONTRACTS.BONDING_CURVE_ROUTER],
        query: {
            enabled: !!address,
            refetchInterval: 5000 // Refresh every 5s to catch approvals
        }
    });

    // Initial System Check Log
    useEffect(() => {
        if (isConnected) {
            addLog('System connected. Wallet active.', 'SYSTEM');
            addLog(`Oracle connection established: ${DUCK_SIGNALS_ADDRESS.slice(0, 8)}...`, 'SYSTEM');
        }
    }, [isConnected]);

    const addLog = (msg: string, type: 'INFO' | 'SUCCESS' | 'WARNING' | 'ERROR' | 'SYSTEM' | 'ACTION' = 'INFO') => {
        const time = new Date().toLocaleTimeString('en-US', { hour12: false });
        const colors: { [key: string]: string } = {
            INFO: 'text-gray-400',
            SUCCESS: 'text-green-400',
            WARNING: 'text-yellow-400',
            ERROR: 'text-red-500 font-bold',
            SYSTEM: 'text-blue-400',
            ACTION: 'text-purple-400 font-bold'
        };
        setLogs(prev => [`<span class="opacity-50 mr-2">[${time}]</span><span class="${colors[type]}">${msg}</span>`, ...prev].slice(0, 200));
    };

    // Watch for Real On-Chain Signals
    useWatchContractEvent({
        address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
        abi: DUCK_SIGNALS_ABI,
        eventName: 'SignalPosted',
        onLogs: async (events) => {
            for (const event of events) {
                if (!event.args || !('signalType' in event.args)) continue;

                const args = event.args as { signalType: string; confidence: bigint; price: bigint; agent: string };
                const { signalType, confidence, price } = args;

                const confNum = Number(confidence);
                const priceFormatted = (Number(price) / 1e18).toFixed(6);

                addLog(`Signal Detected: ${signalType} @ ${priceFormatted} (Conf: ${confNum}%)`, 'INFO');

                if (isArmed) {
                    if (confNum >= confidenceThreshold) {
                        if (signalType === 'BUY') {
                            handleAutoBuy();
                        } else if (signalType === 'SELL') {
                            handleAutoSell();
                        } else {
                            addLog(`Action Skipped: Signal type ${signalType} not configured`, 'WARNING');
                        }
                    } else {
                        addLog(`Action Skipped: Confidence ${confNum}% below threshold ${confidenceThreshold}%`, 'WARNING');
                    }
                }
            }
        },
    });

    const handleAutoBuy = async () => {
        addLog(`Initiating Auto-Buy for ${tradeAmount} MON...`, 'ACTION');
        try {
            await executeBuy(targetToken.address, tradeAmount, 0n, 5);
            addLog(`TX Submitted: Swapping ${tradeAmount} MON for ${targetToken.symbol}`, 'SUCCESS');
            addToHistory('BUY', `${tradeAmount} MON`, priceData.priceInMon.toFixed(6));
        } catch (error: any) {
            addLog(`Execution Failed: ${error.message || 'User rejected/Network error'}`, 'ERROR');
        }
    };

    const handleAutoSell = async () => {
        const balance = tokenBalance?.formatted || '0';
        if (parseFloat(balance) <= 0) {
            addLog(`Sell Skipped: Insufficient ${targetToken.symbol} balance (${balance})`, 'WARNING');
            return;
        }

        const sellAmount = tradeAmount;

        addLog(`Initiating Auto-Sell for ${sellAmount} ${targetToken.symbol}...`, 'ACTION');

        try {
            const amountWei = BigInt(parseFloat(sellAmount) * 1e18); // Assuming 18 decimals
            const currentAllowance = allowance ? BigInt(allowance.toString()) : 0n;

            if (currentAllowance < amountWei) {
                addLog(`Approval Needed: Current allowance ${currentAllowance} < ${amountWei}`, 'WARNING');
                addLog(`Sending Approve TX...`, 'ACTION');
                await approveToken(targetToken.address, sellAmount);
                addLog(`Approval TX Sent. Waiting for confirmation...`, 'INFO');
            } else {
                await executeSell(targetToken.address, sellAmount, 0n, 5);
                addLog(`TX Submitted: Selling ${sellAmount} ${targetToken.symbol}`, 'SUCCESS');
                addToHistory('SELL', `${sellAmount} ${targetToken.symbol}`, priceData.priceInMon.toFixed(6));
            }
        } catch (error: any) {
            addLog(`Execution Failed: ${error.message || 'Error'}`, 'ERROR');
        }
    };

    const addToHistory = (type: string, amount: string, price: string) => {
        setTradeHistory(prev => [{ type, amount, price, time: Date.now() }, ...prev].slice(0, 5));
    }

    const toggleArm = () => {
        if (!isConnected) return;

        if (isArmed) {
            setIsArmed(false);
            addLog('Agent DISARMED. Auto-execution stopped.', 'SYSTEM');
        } else {
            setIsArmed(true);
            addLog(`Agent ARMED. Monitoring for ${targetToken.symbol} signals > ${confidenceThreshold}% confidence.`, 'ACTION');
        }
    };

    if (!isConnected) return (
        <div style={styles.card} className="max-w-md mx-auto text-center p-12">
            <Wallet size={48} className="mx-auto mb-6 text-purple-400" />
            <h3 className="text-xl font-bold mb-2 text-white">Connect Wallet</h3>
            <p className="text-gray-400">Access the AI Trading Terminal by connecting your Monad wallet.</p>
        </div>
    );

    return (
        <section className="max-w-7xl mx-auto px-4">

            {/* 1. CONTROL CENTER CARD */}
            <div style={styles.card}>
                <div style={styles.header}>
                    <div className="flex items-center gap-3">
                        <Cpu size={24} className="text-purple-400" />
                        <div>
                            <h3 className="text-lg font-bold text-white leading-none">AI Trading Terminal</h3>
                            <p className="text-xs text-gray-400 mt-1">Autonomous Execution Engine</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${isArmed ? 'bg-red-500/20 border-red-500/50' : 'bg-gray-800 border-gray-700'}`}>
                            <div className={`w-2 h-2 rounded-full ${isArmed ? 'bg-red-500 animate-pulse' : 'bg-gray-500'}`} />
                            <span className={`text-xs font-bold ${isArmed ? 'text-red-400' : 'text-gray-400'}`}>{isArmed ? 'ARMED' : 'DISARMED'}</span>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/10 border border-green-500/30">
                            <Activity size={12} className="text-green-400" />
                            <span className="text-xs font-bold text-green-400">RPC ONLINE</span>
                        </div>
                    </div>
                </div>

                <div style={styles.body} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Left: Token & Balance */}
                    <div>
                        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 block">Target Asset</label>

                        <div className="relative mb-6">
                            <button
                                onClick={() => !isArmed && setIsTokenDropdownOpen(!isTokenDropdownOpen)}
                                disabled={isArmed}
                                className={`w-full p-4 rounded-xl flex items-center justify-between border transition-all ${isArmed ? 'bg-black/20 border-white/5 opacity-50 cursor-not-allowed' : 'bg-white/5 border-white/10 hover:border-purple-500/50'}`}
                            >
                                <div className="flex items-center gap-4">
                                    <div className="text-left">
                                        <div className="font-bold text-white text-lg">{targetToken.symbol}</div>
                                        <div className="text-xs text-gray-400">{targetToken.name}</div>
                                    </div>
                                </div>
                                <ChevronDown size={14} className="text-gray-500" />
                            </button>

                            {isTokenDropdownOpen && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-[#1a1a23] border border-white/10 rounded-xl overflow-hidden z-20 shadow-2xl">
                                    {POPULAR_MONAD_TOKENS.map(token => (
                                        <button
                                            key={token.address}
                                            onClick={() => { setTargetToken(token); setIsTokenDropdownOpen(false); }}
                                            className="w-full text-left px-4 py-3 hover:bg-white/5 flex items-center gap-3 transition-colors"
                                        >
                                            <span className="text-sm text-gray-300 font-medium">{token.symbol}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                                <div className="text-xs text-gray-400 mb-1">Live Price</div>
                                <div className="text-xl font-bold text-white font-mono">
                                    {priceData.priceInMon.toFixed(6)} <span className="text-sm text-gray-500">MON</span>
                                </div>
                                <div className="text-xs text-green-400 mt-1">
                                    ${(priceData.priceInMon * (priceData.monUsdRate || 0)).toFixed(4)}
                                </div>
                            </div>
                            <div className="p-4 rounded-xl bg-white/5 border border-white/5">
                                <div className="text-xs text-gray-400 mb-1">Your Balance</div>
                                <div className="text-xl font-bold text-white font-mono">
                                    {tokenBalance ? Number(tokenBalance.formatted).toFixed(2) : '0.00'}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">{targetToken.symbol}</div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Controls & Arm */}
                    <div className="flex flex-col justify-between">
                        <div>
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4 block">Configuration</label>

                            <div className="space-y-6">
                                <div>
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-gray-300">Min. Confidence Threshold</span>
                                        <span className="text-purple-400 font-bold">{confidenceThreshold}%</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="50" max="95" step="5"
                                        value={confidenceThreshold}
                                        onChange={(e) => setConfidenceThreshold(Number(e.target.value))}
                                        disabled={isArmed}
                                        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
                                    />
                                </div>

                                <div>
                                    <div className="flex justify-between text-sm mb-2">
                                        <span className="text-gray-300">Trade Amount (Units)</span>
                                        <span className="text-white font-mono font-bold">{tradeAmount}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        {['1', '5', '10', '50', '100'].map(amt => (
                                            <button
                                                key={amt}
                                                onClick={() => setTradeAmount(amt)}
                                                disabled={isArmed}
                                                className={`flex-1 py-1.5 text-xs rounded-lg border transition-all ${tradeAmount === amt ? 'bg-purple-500 text-white border-purple-500' : 'bg-transparent border-white/10 text-gray-400 hover:border-white/20'}`}
                                            >
                                                {amt}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button
                            onClick={toggleArm}
                            className={`w-full py-4 mt-6 rounded-xl font-bold text-lg tracking-wide transition-all transform active:scale-[0.98] flex items-center justify-center gap-3 shadow-lg relative overflow-hidden ${isArmed
                                ? 'bg-gradient-to-r from-red-600 to-red-500 text-white shadow-red-500/20'
                                : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-purple-500/30 hover:shadow-purple-500/50'
                                }`}
                        >
                            {isArmed ? <Lock size={20} /> : <Power size={20} />}
                            {isArmed ? 'DISARM SYSTEM' : 'ARM AGENT'}
                        </button>
                    </div>
                </div>
            </div>

            {/* 2. TERMINAL & MARKET INTEL GRID */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* TERMINAL CARD */}
                <div style={styles.card} className="lg:col-span-2">
                    <div style={styles.header}>
                        <div className="flex items-center gap-3">
                            <Terminal size={18} className="text-gray-400" />
                            <h3 className="text-sm font-bold text-gray-300">System Logs</h3>
                        </div>
                        <div className="flex gap-1.5 opacity-50">
                            <div className="w-2.5 h-2.5 rounded-full bg-red-500" />
                            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
                            <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                        </div>
                    </div>
                    <div className="p-0 bg-[#0a0a0f] h-[400px]">
                        <TerminalLog logs={logs} />
                    </div>
                </div>

                {/* SIGNALS & HISTORY CARD */}
                <div className="flex flex-col gap-6">
                    {/* Recent Executions */}
                    <div style={styles.card} className="flex-1 mb-0">
                        <div style={styles.header}>
                            <div className="flex items-center gap-3">
                                <History size={18} className="text-gray-400" />
                                <h3 className="text-sm font-bold text-gray-300">Execution History</h3>
                            </div>
                        </div>
                        <div style={styles.body} className="min-h-[150px]">
                            <div className="space-y-2">
                                {tradeHistory.map((trade, i) => (
                                    <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-white/5 text-sm">
                                        <div className="flex items-center gap-2">
                                            {trade.type === 'BUY'
                                                ? <div className="text-green-400 font-bold bg-green-500/10 px-1.5 rounded text-xs">BUY</div>
                                                : <div className="text-red-400 font-bold bg-red-500/10 px-1.5 rounded text-xs">SELL</div>}
                                            <span className="text-gray-300">{trade.amount}</span>
                                        </div>
                                        <span className="text-gray-500 text-xs">@ {trade.price}</span>
                                    </div>
                                ))}
                                {tradeHistory.length === 0 && (
                                    <div className="text-center text-gray-600 text-xs py-8">No trades executed yet</div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Live Signals */}
                    <div style={styles.card} className="flex-1 mb-0">
                        <div style={styles.header}>
                            <div className="flex items-center gap-3">
                                <Target size={18} className="text-gray-400" />
                                <h3 className="text-sm font-bold text-gray-300">Signal Feed</h3>
                            </div>
                        </div>
                        <div style={styles.body}>
                            <div className="space-y-2">
                                {signals?.slice(0, 3).map((signal, i) => (
                                    <div key={i} className="flex justify-between items-center p-2 rounded-lg bg-white/5 border border-white/5">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${signal.signalType === 'BUY' ? 'bg-green-500' : 'bg-red-500'}`} />
                                            <span className="text-xs font-bold text-gray-300">{signal.signalType}</span>
                                        </div>
                                        <span className="text-xs font-mono text-purple-300">{Number(signal.confidence)}% Conf</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AutoTrader;
